export interface IDicomImage {
  rows: number;
  columns: number;
  frames: number;
  samplesPerPixel: number;
  bitsAllocated: number;
  bitsStored: number;
  pixelRepresentation: number;
  photometricInterpretation: string;
  windowCenter?: number;
  windowWidth?: number;
  rescaleIntercept: number;
  rescaleSlope: number;
  pixelDataOffset: number;
  pixelDataLength: number;
  arrayBuffer: ArrayBuffer;
}

interface IDicomElement {
  tag: string;
  vr?: string;
  valueOffset: number;
  valueLength: number;
}

const LONG_VR = new Set([
  'OB',
  'OD',
  'OF',
  'OL',
  'OV',
  'OW',
  'SQ',
  'UC',
  'UN',
  'UR',
  'UT',
]);

const TRANSFER_SYNTAX_UID = '0002,0010';
const PIXEL_DATA = '7fe0,0010';
const EXPLICIT_LITTLE_ENDIAN = '1.2.840.10008.1.2.1';
const IMPLICIT_LITTLE_ENDIAN = '1.2.840.10008.1.2';

export async function isDicomBlob(blob: Blob, fileName?: string): Promise<boolean> {
  if (isDicomFileName(fileName) || blob.type === 'application/dicom') {
    return true;
  }

  const header = await blob.slice(128, 132).arrayBuffer();
  return ascii(new DataView(header), 0, 4) === 'DICM';
}

export function isDicomFileName(fileName?: string): boolean {
  const normalized = fileName?.toLowerCase() || '';
  return (
    normalized.endsWith('.dcm') ||
    normalized.endsWith('.dicom') ||
    normalized.endsWith('.ima')
  );
}

export function parseDicom(arrayBuffer: ArrayBuffer): IDicomImage {
  const view = new DataView(arrayBuffer);
  const startOffset = ascii(view, 128, 4) === 'DICM' ? 132 : 0;
  const elements = new Map<string, IDicomElement>();
  let offset = startOffset;
  let explicitVr = true;
  let transferSyntax: string | undefined;

  while (offset + 8 <= view.byteLength) {
    const element = readElement(view, offset, explicitVr);

    if (!element || element.valueLength < 0) {
      break;
    }

    elements.set(element.tag, element);

    if (element.tag === TRANSFER_SYNTAX_UID) {
      transferSyntax = readString(view, element);
    }

    offset = element.valueOffset + element.valueLength + (element.valueLength % 2);

    if (element.tag.startsWith('0002,') && offset + 8 <= view.byteLength) {
      const nextGroup = view.getUint16(offset, true);

      if (nextGroup !== 0x0002) {
        explicitVr = transferSyntax !== IMPLICIT_LITTLE_ENDIAN;
      }
    }

    if (element.tag === PIXEL_DATA) {
      break;
    }
  }

  if (
    transferSyntax &&
    transferSyntax !== EXPLICIT_LITTLE_ENDIAN &&
    transferSyntax !== IMPLICIT_LITTLE_ENDIAN
  ) {
    throw new Error(`Unsupported DICOM transfer syntax: ${transferSyntax}`);
  }

  const pixelData = elements.get(PIXEL_DATA);

  if (!pixelData) {
    throw new Error('DICOM pixel data was not found.');
  }

  const rows = readNumber(view, elements.get('0028,0010'));
  const columns = readNumber(view, elements.get('0028,0011'));
  const samplesPerPixel = readNumber(view, elements.get('0028,0002')) || 1;
  const bitsAllocated = readNumber(view, elements.get('0028,0100')) || 16;
  const bitsStored = readNumber(view, elements.get('0028,0101')) || bitsAllocated;
  const pixelRepresentation = readNumber(view, elements.get('0028,0103')) || 0;
  const frames = Math.max(
    1,
    Math.trunc(readDecimal(view, elements.get('0028,0008')) || 1),
  );

  if (!rows || !columns) {
    throw new Error('DICOM image dimensions were not found.');
  }

  return {
    rows,
    columns,
    frames,
    samplesPerPixel,
    bitsAllocated,
    bitsStored,
    pixelRepresentation,
    photometricInterpretation:
      readString(view, elements.get('0028,0004')) || 'MONOCHROME2',
    windowCenter: readDecimal(view, elements.get('0028,1050')),
    windowWidth: readDecimal(view, elements.get('0028,1051')),
    rescaleIntercept: readDecimal(view, elements.get('0028,1052')) || 0,
    rescaleSlope: readDecimal(view, elements.get('0028,1053')) || 1,
    pixelDataOffset: pixelData.valueOffset,
    pixelDataLength: pixelData.valueLength,
    arrayBuffer,
  };
}

export function dicomFrameToDataUrl(
  dicom: IDicomImage,
  frameIndex: number,
  windowCenter?: number,
  windowWidth?: number,
): string {
  const canvas = document.createElement('canvas');
  renderDicomFrameToCanvas(dicom, frameIndex, canvas, windowCenter, windowWidth);
  return canvas.toDataURL('image/png');
}

export function dicomFrameToPngBlob(
  dicom: IDicomImage,
  frameIndex: number,
  windowCenter?: number,
  windowWidth?: number,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  renderDicomFrameToCanvas(dicom, frameIndex, canvas, windowCenter, windowWidth);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Unable to create PNG from DICOM frame.'));
      }
    }, 'image/png');
  });
}

export function renderDicomFrameToCanvas(
  dicom: IDicomImage,
  frameIndex: number,
  canvas: HTMLCanvasElement,
  windowCenter?: number,
  windowWidth?: number,
) {
  if (frameIndex < 0 || frameIndex >= dicom.frames) {
    throw new Error('DICOM frame index is out of range.');
  }

  canvas.width = dicom.columns;
  canvas.height = dicom.rows;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas rendering is not available.');
  }

  const imageData = ctx.createImageData(dicom.columns, dicom.rows);
  fillImageData(imageData, dicom, frameIndex, windowCenter, windowWidth);
  ctx.putImageData(imageData, 0, 0);
}

function fillImageData(
  imageData: ImageData,
  dicom: IDicomImage,
  frameIndex: number,
  windowCenter?: number,
  windowWidth?: number,
) {
  if (dicom.samplesPerPixel === 3 && dicom.bitsAllocated === 8) {
    fillRgbImageData(imageData, dicom, frameIndex);
    return;
  }

  if (dicom.samplesPerPixel !== 1 || ![8, 16].includes(dicom.bitsAllocated)) {
    throw new Error('Only uncompressed grayscale and 8-bit RGB DICOM images are supported.');
  }

  fillGrayscaleImageData(imageData, dicom, frameIndex, windowCenter, windowWidth);
}

function fillRgbImageData(
  imageData: ImageData,
  dicom: IDicomImage,
  frameIndex: number,
) {
  const pixelsPerFrame = dicom.rows * dicom.columns;
  const frameOffset = dicom.pixelDataOffset + frameIndex * pixelsPerFrame * 3;
  const source = new Uint8Array(dicom.arrayBuffer, frameOffset, pixelsPerFrame * 3);
  const rgba = imageData.data;

  for (let i = 0, j = 0; i < source.length; i += 3, j += 4) {
    rgba[j] = source[i];
    rgba[j + 1] = source[i + 1];
    rgba[j + 2] = source[i + 2];
    rgba[j + 3] = 255;
  }
}

function fillGrayscaleImageData(
  imageData: ImageData,
  dicom: IDicomImage,
  frameIndex: number,
  windowCenter?: number,
  windowWidth?: number,
) {
  const view = new DataView(dicom.arrayBuffer);
  const bytesPerSample = dicom.bitsAllocated / 8;
  const pixelsPerFrame = dicom.rows * dicom.columns;
  const frameOffset = dicom.pixelDataOffset + frameIndex * pixelsPerFrame * bytesPerSample;
  const pixels = new Float32Array(pixelsPerFrame);
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < pixelsPerFrame; i++) {
    const offset = frameOffset + i * bytesPerSample;
    let value =
      dicom.bitsAllocated === 16
        ? read16BitPixel(view, offset, dicom.pixelRepresentation === 1)
        : view.getUint8(offset);

    value = value * dicom.rescaleSlope + dicom.rescaleIntercept;
    pixels[i] = value;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }

  const center = windowCenter ?? dicom.windowCenter ?? (min + max) / 2;
  const measuredWidth = max - min || 1;
  const width = Math.max(1, windowWidth ?? dicom.windowWidth ?? measuredWidth);
  const low = center - width / 2;
  const photometric = dicom.photometricInterpretation.toUpperCase();
  const invert = photometric === 'MONOCHROME1';
  const rgba = imageData.data;

  for (let i = 0, j = 0; i < pixels.length; i++, j += 4) {
    let grayscale = Math.round(((pixels[i] - low) / width) * 255);
    grayscale = Math.max(0, Math.min(255, grayscale));
    grayscale = invert ? 255 - grayscale : grayscale;

    rgba[j] = grayscale;
    rgba[j + 1] = grayscale;
    rgba[j + 2] = grayscale;
    rgba[j + 3] = 255;
  }
}

function readElement(
  view: DataView,
  offset: number,
  explicitVr: boolean,
): IDicomElement | undefined {
  const group = view.getUint16(offset, true);
  const element = view.getUint16(offset + 2, true);
  const tag = `${group.toString(16).padStart(4, '0')},${element
    .toString(16)
    .padStart(4, '0')}`;

  if (explicitVr) {
    const vr = ascii(view, offset + 4, 2);

    if (LONG_VR.has(vr)) {
      const valueLength = view.getUint32(offset + 8, true);
      const valueOffset = offset + 12;

      if (valueLength === 0xffffffff) {
        const valueEnd = findUndefinedLengthElementEnd(view, valueOffset);

        return {
          tag,
          vr,
          valueOffset,
          valueLength: valueEnd - valueOffset,
        };
      }

      return {
        tag,
        vr,
        valueOffset,
        valueLength,
      };
    }

    return {
      tag,
      vr,
      valueOffset: offset + 8,
      valueLength: view.getUint16(offset + 6, true),
    };
  }

  const valueLength = view.getUint32(offset + 4, true);
  const valueOffset = offset + 8;

  if (valueLength === 0xffffffff) {
    const valueEnd = findUndefinedLengthElementEnd(view, valueOffset);

    return {
      tag,
      valueOffset,
      valueLength: valueEnd - valueOffset,
    };
  }

  return {
    tag,
    valueOffset,
    valueLength,
  };
}

function findUndefinedLengthElementEnd(view: DataView, offset: number): number {
  let cursor = offset;

  while (cursor + 8 <= view.byteLength) {
    const group = view.getUint16(cursor, true);
    const element = view.getUint16(cursor + 2, true);

    if (group === 0xfffe && element === 0xe0dd) {
      return cursor + 8;
    }

    cursor += 2;
  }

  return view.byteLength;
}

function read16BitPixel(view: DataView, offset: number, signed: boolean): number {
  return signed ? view.getInt16(offset, true) : view.getUint16(offset, true);
}

function readNumber(view: DataView, element?: IDicomElement): number | undefined {
  if (!element || element.valueLength < 2) {
    return undefined;
  }

  return view.getUint16(element.valueOffset, true);
}

function readDecimal(view: DataView, element?: IDicomElement): number | undefined {
  const value = readString(view, element).split('\\')[0];
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function readString(view: DataView, element?: IDicomElement): string {
  if (!element || element.valueLength <= 0) {
    return '';
  }

  return ascii(view, element.valueOffset, element.valueLength)
    .replace(/\0/g, '')
    .trim();
}

function ascii(view: DataView, offset: number, length: number): string {
  let value = '';
  const end = Math.min(view.byteLength, offset + length);

  for (let i = offset; i < end; i++) {
    value += String.fromCharCode(view.getUint8(i));
  }

  return value;
}
